import { useAuth } from '@/context/AuthContext';
import { useState, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';

const CSIProgrammesData = ({ period }) => {
    const { apiFetch } = useAuth();
    const [csiData, setCsiData] = useState([]);

    function getMonthYear(period) {
        if (!period) return { monthName: '', year: '', prevYear: '' };
        const periodStr = period.toString().padStart(6, '0');
        if (periodStr.length !== 6) return { monthName: '', year: '', prevYear: '' };
        const year = periodStr.substring(0, 4);
        const prevYear = (parseInt(year, 10) - 1).toString();
        const month = periodStr.substring(4, 6);
        const monthNames = [
            '', 'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const monthIndex = parseInt(month, 10);
        return {
            monthName: monthNames[monthIndex],
            year,
            prevYear
        };
    }

    const { monthName, year, prevYear } = getMonthYear(period);

    useEffect(() => {
        let isCancelled = false;

        const fetchCSIData = async () => {
            try {
                const response = await apiFetch('/api/Marketing/csi-programmes');
                const data = await response.json();
                if (!isCancelled) {
                    if (data.success && Array.isArray(data.data)) {
                        setCsiData(data.data);
                    } else {
                        setCsiData([]);
                    }
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
                if (!isCancelled) {
                    setCsiData([]);
                }
            }
        };

        fetchCSIData();

        return () => {
            isCancelled = true;
        };
    }, [apiFetch]);

    const tableData = useMemo(() => {
        if (!period) return [];

        const source = Array.isArray(csiData) ? csiData : [];
        const periodStr = period.toString().padStart(6, '0');
        const currentYear = parseInt(periodStr.slice(0, 4), 10);
        const currentMonth = parseInt(periodStr.slice(4, 6), 10);
        const previousYear = currentYear - 1;

        const rows = [
            { name: 'Educational Support', key: 'educationalSupport' },
            { name: 'Community Upliftment', key: 'communityUpliftment' },
            { name: 'Sport for Youth', key: 'sportForYouth' },
            { name: 'Community Healthcare', key: 'primaryHealthcare' },
        ];

        return rows.map((row) => {
            const currentMonthData = source
                .filter((item) =>
                    parseInt(item.period.toString().slice(0, 4), 10) === currentYear &&
                    parseInt(item.period.toString().slice(4, 6), 10) === currentMonth
                )
                .reduce((sum, item) => sum + (item[row.key] || 0), 0);

            const yearToDateData = source
                .filter((item) => parseInt(item.period.toString().slice(0, 4), 10) === currentYear)
                .reduce((sum, item) => sum + (item[row.key] || 0), 0);

            const previousYearData = source
                .filter((item) => parseInt(item.period.toString().slice(0, 4), 10) === previousYear)
                .reduce((sum, item) => sum + (item[row.key] || 0), 0);

            return {
                name: row.name,
                month: currentMonthData,
                yearToDate: yearToDateData,
                previousYear: previousYearData,
            };
        });
    }, [csiData, period]);

    function formatNumberWithSpace(value) {
        if (value == null || isNaN(value)) return '0';
        return value
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    return (
        <table className="csi-milestones-table">
            <thead>
                <tr>
                    <th>Number of CSI Programmes Implemented</th>
                    <th>
                        Month<br />
                        {monthName} {year}
                    </th>
                    <th>
                        Year to Date<br />
                        {monthName} {year}
                    </th>
                    <th>
                        Year Ended December<br />
                        {prevYear}
                    </th>
                </tr>
            </thead>
            <tbody>
                {tableData.map((row, index) => (
                    <tr key={index}>
                        <td>{row.name}</td>
                        <td>{formatNumberWithSpace(row.month)}</td>
                        <td>{formatNumberWithSpace(row.yearToDate)}</td>
                        <td>{formatNumberWithSpace(row.previousYear)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default CSIProgrammesData;